// src/services/azureProjectWebsiteService.js
// Azure Table Storage Service für die externe Projektwebsite eines Projekts
// (US-WEBP-001). Muster: azureSitzungenService.js / azurePendenzenService.js
// (projektbezogene Tabelle, PartitionKey = projectUuid, do/while-Paginierung,
// automatische Tabellenerstellung).
//
// Datenmodell (Tabelle `projectWebsite`, PartitionKey = projectUuid = RowKey des
// Projekts in der Tabelle `projects`) — Diskriminierung über RowKey-Präfix:
//   RowKey `config`            → Baustein-Aktivierung, Projekt-Referenzen, Zugangs-
//                                schutz (Passwort-HASH, nie Klartext)
//   RowKey `block_planstand`   → Kopfangaben des Bausteins "Planstand"
//   RowKey `plan_<id>`         → ein Plan im Planstand-Register (Nummer, Titel,
//                                Quelle = verfassendes Büro, Gruppe = Gliederung,
//                                SortOrder = Reihenfolge)
//   RowKey `planver_<linkId>`  → ein Index/Stand dieses Plans; der RowKey IST die
//                                Link-ID, damit der Microservice einen Link per
//                                Punktabfrage auflöst (US-WEBP-002)
//   RowKey `addr_<id>`         → eine Zeile der Adressliste
// Die vier Platzhalter-Bausteine (Terminplan, Ferienkalender, Aktueller Plansatz,
// Bilder) haben bewusst noch KEINE eigenen Entities — nur ihr Aktiv-Flag in
// `config.Blocks`. Der GL-Schalter "Projektwebsite" selbst liegt nicht hier,
// sondern als Property `Projektwebsite` auf dem Projekt (Tabelle `projects`),
// damit die Projektliste ihn ohne Zusatzabfrage kennt (ADR 0045).

const CONFIG = {
  storageAccount: "mfprojects",
  tableName: "projectWebsite",
  sasToken:
    "sv=2024-11-04&ss=t&srt=sco&sp=rwdlacu&se=2035-07-04T17:08:25Z&st=2025-07-04T09:08:25Z&spr=https&sig=X7s%2BFIyYcKvRzSxRNAp5BUJEe2YUcg8nojkn9fIfcTY%3D",
};

const getBaseUrl = () =>
  `https://${CONFIG.storageAccount}.table.core.windows.net/${CONFIG.tableName}`;

const CONFIG_ROW_KEY = "config";
const PLANSTAND_ROW_KEY = "block_planstand";
const ADDRESS_PREFIX = "addr_";
const PLAN_PREFIX = "plan_";
const PLANVERSION_PREFIX = "planver_";

// Öffentliche Basis-Adresse der externen Projektwebsite. Der Planstand-Link wird
// daraus als `<base>/<projektnummer>/planstand/<linkId>` gebaut — bewusst die reine
// Projektnummer und nicht der Slug: kürzester QR-Code, am wenigsten abzutippen, und
// die API löst beide auf (nur die führende Nummer ist dort autoritativ).
// Konfigurierbar per Env. Die Guard-Form (statt direkt `import.meta.env.X || "…"`)
// hält das Modul auch ausserhalb von Vite ladbar — z.B. für Diagnose-Skripte unter node.
const BASE_URL_FROM_ENV =
  (typeof import.meta !== "undefined" && import.meta.env
    ? import.meta.env.VITE_PROJEKTWEBSITE_BASE_URL
    : "") || "https://malheur-fortuna.ch/active";
const PROJEKTWEBSITE_BASE_URL = BASE_URL_FROM_ENV.replace(/\/+$/, "");

// Öffentlicher Link auf genau diesen Planstand (Plan + Index).
export const buildPlanstandUrl = (projectCode, linkId) =>
  `${PROJEKTWEBSITE_BASE_URL}/${projectCode || ""}/planstand/${linkId || ""}`;

// Dateiname des QR-Code-PNG zu genau diesem Planstand. Der Index gehört zwingend
// hinein: jeder Indexstand hat einen eigenen Link, und ein ausgedruckter Code darf
// auf der Baustelle nie dem falschen Stand zugeordnet werden können. Zeichen, die
// Windows/macOS in Dateinamen verbieten, werden ersetzt; Umlaute bleiben erhalten.
// Gesäubert wird je Segment, damit die Trennzeichen "_" sauber stehen bleiben.
export const buildPlanstandDateiname = (plan, version) => {
  const saeubern = (teil) =>
    String(teil ?? "")
      .trim()
      // eslint-disable-next-line no-control-regex
      .replace(/[\x5c\/:*?"<>|\x00-\x1f]/g, "-")
      .replace(/\s+/g, "-")
      .replace(/-{2,}/g, "-")
      .replace(/^[.\s_-]+|[.\s_-]+$/g, "");
  const teile = [saeubern(plan?.nummer), saeubern(plan?.titel)].filter(Boolean);
  if (teile.length === 0) teile.push("Planstand");
  teile.push(`Index-${saeubern(version?.index) || "ohne"}`);
  return `${teile.join("_")}.png`;
};

// Bausteine der Projektwebsite. `placeholder: true` heisst: aktivierbar, aber
// noch ohne erfassbare Inhalte (bewusster Scope von US-WEBP-001).
export const WEBSITE_BLOCKS = [
  { key: "planstand", label: "Planstand", placeholder: false },
  { key: "adressliste", label: "Adressliste", placeholder: false },
  { key: "terminplan", label: "Terminplan", placeholder: true },
  { key: "ferienkalender", label: "Ferienkalender", placeholder: true },
  { key: "plansatz", label: "Aktueller Plansatz", placeholder: true },
  { key: "bilder", label: "Bilder", placeholder: true },
];

export const generateAdressId = () =>
  `${ADDRESS_PREFIX}${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

const generatePlanId = () =>
  `${PLAN_PREFIX}${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

const cleanValue = (value) => {
  if (value === null || value === undefined) return "";
  return String(value);
};

const emptyBlocks = () =>
  WEBSITE_BLOCKS.reduce((acc, b) => ({ ...acc, [b.key]: false }), {});

export const emptyWebsiteData = () => ({
  blocks: emptyBlocks(),
  planstand: { beschreibung: "", datum: "", ablageLink: "" },
  // Planstand-Register: je Plan die Nummer/Titel plus alle Indexstände
  // (neuester zuerst). Wird NICHT über saveProjectWebsite geschrieben, sondern
  // über eigene Sofort-Operationen — siehe createWebsitePlan & Co.
  plaene: [],
  adressen: [],
  // Zugangsschutz: `gesetzt` sagt nur, OB ein Passwort hinterlegt ist. Der Klartext
  // wird nie gespeichert und ist nicht wiederherstellbar.
  password: { gesetzt: false, gesetztAm: "", gesetztVon: "" },
  updatedAt: "",
  updatedBy: "",
});

export const emptyAdresse = () => ({
  id: generateAdressId(),
  firma: "",
  person: "",
  telefon: "",
  email: "",
  adresse: "",
  ferienvertretung: "",
});

// --- Zugangsschutz: Passwort-Hashing -----------------------------------------
// Das Passwort für die externe Seite wird NIE im Klartext gespeichert, sondern nur
// als PBKDF2-SHA256-Hash mit Zufallssalz. Grund: der SAS-Token des Frontends ist
// account-weit — wer die Tabelle lesen kann, käme sonst an das Klartext-Passwort
// (das Externe erfahrungsgemäss auch anderswo verwenden). Format ist
// selbstbeschreibend, damit der Microservice die Parameter nicht raten muss:
//
//   pbkdf2-sha256$<iterations>$<salt-base64>$<hash-base64>
//
// Der Microservice prüft eine Eingabe, indem er sie mit demselben Salz und derselben
// Iterationszahl ableitet und die Hashes zeitkonstant vergleicht.
export const PASSWORD_ALGO = "pbkdf2-sha256";
export const PASSWORD_ITERATIONS = 600000; // OWASP-Empfehlung für PBKDF2-SHA256
export const PASSWORD_MIN_LENGTH = 10;

const getCrypto = () => {
  const c = typeof globalThis !== "undefined" ? globalThis.crypto : null;
  if (!c || !c.subtle) {
    throw new Error(
      "Passwort-Verschlüsselung nicht verfügbar (WebCrypto fehlt — HTTPS nötig)."
    );
  }
  return c;
};

const toBase64 = (bytes) => {
  let binary = "";
  const view = new Uint8Array(bytes);
  for (let i = 0; i < view.length; i += 1) binary += String.fromCharCode(view[i]);
  return btoa(binary);
};

const fromBase64 = (value) =>
  Uint8Array.from(atob(value), (ch) => ch.charCodeAt(0));

const deriveBits = async (password, salt, iterations) => {
  const c = getCrypto();
  const key = await c.subtle.importKey(
    "raw",
    new TextEncoder().encode(password),
    "PBKDF2",
    false,
    ["deriveBits"]
  );
  return c.subtle.deriveBits(
    { name: "PBKDF2", salt, iterations, hash: "SHA-256" },
    key,
    256
  );
};

// Klartext-Passwort in den zu speichernden Hash-String umwandeln.
export const hashWebsitePassword = async (password) => {
  const plain = String(password || "");
  if (plain.length < PASSWORD_MIN_LENGTH) {
    throw new Error(
      `Das Passwort muss mindestens ${PASSWORD_MIN_LENGTH} Zeichen lang sein.`
    );
  }
  const salt = getCrypto().getRandomValues(new Uint8Array(16));
  const bits = await deriveBits(plain, salt, PASSWORD_ITERATIONS);
  return `${PASSWORD_ALGO}$${PASSWORD_ITERATIONS}$${toBase64(salt)}$${toBase64(bits)}`;
};

// Gegenprobe (wird in INTRA nur für Tests/Diagnose gebraucht — die eigentliche
// Prüfung macht der Microservice). Vergleich zeitkonstant.
export const verifyWebsitePassword = async (password, stored) => {
  if (!stored) return false;
  const parts = String(stored).split("$");
  if (parts.length !== 4 || parts[0] !== PASSWORD_ALGO) return false;
  const iterations = parseInt(parts[1], 10);
  if (!Number.isFinite(iterations) || iterations <= 0) return false;
  const salt = fromBase64(parts[2]);
  const expected = fromBase64(parts[3]);
  const actual = new Uint8Array(await deriveBits(String(password || ""), salt, iterations));
  if (actual.length !== expected.length) return false;
  let diff = 0;
  for (let i = 0; i < actual.length; i += 1) diff |= actual[i] ^ expected[i];
  return diff === 0;
};

// Vorschlag für ein starkes, gemeinsames Zugangspasswort. Ohne leicht zu
// verwechselnde Zeichen (0/O, 1/l/I), damit es telefonisch weitergegeben werden kann.
export const suggestWebsitePassword = () => {
  const alphabet = "abcdefghijkmnpqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ23456789";
  // Rejection Sampling, damit keine Zeichen häufiger vorkommen als andere.
  const limit = Math.floor(256 / alphabet.length) * alphabet.length;
  const chars = [];
  while (chars.length < 16) {
    for (const b of getCrypto().getRandomValues(new Uint8Array(16))) {
      if (b < limit && chars.length < 16) chars.push(alphabet[b % alphabet.length]);
    }
  }
  return chars.join("").replace(/(.{4})(?=.)/g, "$1-");
};

// --- Planstand-Register: Link-IDs ---------------------------------------------
// Die Link-ID landet in einer URL, die auf gedruckten Plänen steht (QR-Code) und
// nie geändert werden darf. Darum: 16 Zeichen aus [a-z0-9] (~82 Bit, nicht
// erratbar, keine Enumeration möglich), Kleinschreibung und kein Sonderzeichen —
// so ist sie gleichzeitig URL-sicher und als Azure-RowKey zulässig.
const generateLinkId = () => {
  const alphabet = "abcdefghijklmnopqrstuvwxyz0123456789";
  const limit = Math.floor(256 / alphabet.length) * alphabet.length;
  const chars = [];
  while (chars.length < 16) {
    for (const b of getCrypto().getRandomValues(new Uint8Array(16))) {
      if (b < limit && chars.length < 16) chars.push(alphabet[b % alphabet.length]);
    }
  }
  return chars.join("");
};

let tableEnsured = false;
const ensureTableExists = async () => {
  if (tableEnsured) return true;
  try {
    const url = `https://${CONFIG.storageAccount}.table.core.windows.net/Tables?${CONFIG.sasToken}`;
    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json;odata=nometadata",
      },
      body: JSON.stringify({ TableName: CONFIG.tableName }),
    });
    if (response.ok || response.status === 409) {
      tableEnsured = true;
      return true;
    }
    return false;
  } catch (error) {
    console.error("Projektwebsite: Tabelle konnte nicht angelegt werden:", error);
    return false;
  }
};

// Nur bekannte Baustein-Schlüssel übernehmen, fehlende als false ergänzen —
// so bleiben spätere Bausteine rückwärtskompatibel.
const normalizeBlocks = (raw) => {
  let parsed = {};
  if (raw) {
    try {
      const value = typeof raw === "string" ? JSON.parse(raw) : raw;
      if (value && typeof value === "object") parsed = value;
    } catch {
      parsed = {};
    }
  }
  return WEBSITE_BLOCKS.reduce(
    (acc, b) => ({ ...acc, [b.key]: parsed[b.key] === true }),
    {}
  );
};

const mapPlan = (item) => ({
  id: item.RowKey,
  nummer: item.Nummer || "",
  titel: item.Titel || "",
  // Verfassendes Büro. Gehört zum Plan, nicht zum Indexstand — wie Titel und
  // Nummer, und aus demselben Grund: extern gilt immer die aktuelle Fassung.
  // Pläne aus der Zeit vor diesem Feld liefern "".
  quelle: item.Quelle || "",
  // Gliederung des Registers, freier Text (z.B. "Werkleitungen", "Schnitte").
  // Bewusst ein Feld am Plan und keine eigene Entity: eine Gruppe existiert
  // genau so lange, wie ein Plan sie nennt — damit gibt es keine verwaisten
  // Gruppen und keine Referenz, die brechen könnte. "" = ohne Gruppe.
  gruppe: item.Gruppe || "",
  aktuelleVersionId: item.AktuelleVersionId || "",
  sortOrder: typeof item.SortOrder === "number" ? item.SortOrder : 0,
  erstelltAm: item.ErstelltAm || "",
});

const mapPlanVersion = (item) => ({
  id: item.RowKey,
  // Die Link-ID ist der RowKey ohne Präfix — kein zweites Feld, das auseinander
  // laufen könnte.
  linkId: item.RowKey.slice(PLANVERSION_PREFIX.length),
  planId: item.PlanId || "",
  index: item.Index || "",
  datum: item.Datum || "",
  pdfBlobName: item.PdfBlobName || "", // Platzhalter, noch nicht belegt
  erstelltAm: item.ErstelltAm || "",
  erstelltVon: item.ErstelltVon || "",
});

const mapAdresse = (item) => ({
  id: item.RowKey,
  firma: item.Firma || "",
  person: item.Person || "",
  telefon: item.Telefon || "",
  email: item.Email || "",
  adresse: item.Adresse || "",
  ferienvertretung: item.Ferienvertretung || "",
  sortOrder: typeof item.SortOrder === "number" ? item.SortOrder : 0,
});

// Alle Website-Daten eines Projekts laden (eine Partition, vollständig paginiert).
export const getProjectWebsite = async (projectUuid) => {
  if (!projectUuid) return emptyWebsiteData();
  try {
    await ensureTableExists();
    const filter = `&$filter=PartitionKey eq '${projectUuid}'`;
    let allEntries = [];
    let nextPartitionKey = null;
    let nextRowKey = null;
    do {
      let url = `${getBaseUrl()}()?${CONFIG.sasToken}${filter}`;
      if (nextPartitionKey && nextRowKey) {
        url += `&NextPartitionKey=${encodeURIComponent(nextPartitionKey)}`;
        url += `&NextRowKey=${encodeURIComponent(nextRowKey)}`;
      }
      const response = await fetch(url, {
        headers: { Accept: "application/json;odata=nometadata" },
      });
      if (!response.ok) {
        const errorText = await response.text();
        console.error("Azure Error:", errorText);
        throw new Error(`Azure Table Error: ${response.status}`);
      }
      const data = await response.json();
      allEntries = allEntries.concat(data.value || []);
      nextPartitionKey = response.headers.get("x-ms-continuation-NextPartitionKey");
      nextRowKey = response.headers.get("x-ms-continuation-NextRowKey");
    } while (nextPartitionKey && nextRowKey);

    const configEntity = allEntries.find((e) => e.RowKey === CONFIG_ROW_KEY);
    const planstandEntity = allEntries.find((e) => e.RowKey === PLANSTAND_ROW_KEY);
    const adressen = allEntries
      .filter((e) => e.RowKey.startsWith(ADDRESS_PREFIX))
      .map(mapAdresse)
      .sort((a, b) => a.sortOrder - b.sortOrder);

    // Planstand-Register zusammensetzen: Pläne mit ihren Indexständen, neuester
    // zuerst. `veraltet` ergibt sich aus AktuelleVersionId des Plans — nicht aus
    // dem Datum, damit ein nachträglich korrigiertes Datum die Reihenfolge nicht
    // umdreht. Versionen ohne existierenden Plan werden übergangen (Waise).
    const alleVersionen = allEntries
      .filter((e) => e.RowKey.startsWith(PLANVERSION_PREFIX))
      .map(mapPlanVersion);
    // `planver_…` beginnt nicht mit `plan_` (5. Zeichen v statt _), die Präfixe
    // kollidieren also nicht.
    const plaene = allEntries
      .filter((e) => e.RowKey.startsWith(PLAN_PREFIX))
      .map(mapPlan)
      // Bei gleichem SortOrder entscheidet die Erfassungszeit. Pläne aus der
      // Zeit vor `updateWebsitePlanOrder` haben alle den Wert, den sie beim
      // Anlegen bekommen haben — ohne diesen Stichentscheid hinge ihre
      // Reihenfolge an der Rückgabereihenfolge von Azure.
      .sort(
        (a, b) =>
          a.sortOrder - b.sortOrder ||
          (a.erstelltAm || "").localeCompare(b.erstelltAm || "")
      )
      .map((plan) => {
        const versionen = alleVersionen
          .filter((v) => v.planId === plan.id)
          .sort((a, b) => (b.erstelltAm || "").localeCompare(a.erstelltAm || ""));
        // Fallback, falls AktuelleVersionId fehlt (z.B. abgebrochener Schreibvorgang):
        // der jüngste Eintrag gilt als aktuell. Dieselbe Regel gilt für den
        // Microservice — sonst wäre bei leerem Feld JEDER Stand veraltet.
        const neuesteVersionId = versionen[0]?.id || "";
        const aktuelleVersionId = plan.aktuelleVersionId || neuesteVersionId;
        return {
          ...plan,
          aktuelleVersionId,
          // Zeigt der aktive Stand nicht auf den jüngsten Eintrag, ist entweder ein
          // Schreibvorgang abgebrochen (dann steht der NEUE Stand fälschlich als
          // veraltet da — gefährliche Richtung) oder jemand hat bewusst einen
          // früheren Stand reaktiviert. Beides muss die Maske sichtbar machen.
          neuesteVersionId,
          versionen: versionen.map((v) => ({
            ...v,
            veraltet: v.id !== aktuelleVersionId,
          })),
        };
      });

    return {
      blocks: normalizeBlocks(configEntity?.Blocks),
      planstand: {
        beschreibung: planstandEntity?.Beschreibung || "",
        datum: planstandEntity?.Datum || "",
        ablageLink: planstandEntity?.AblageLink || "",
      },
      plaene,
      adressen,
      // Der Hash selbst verlässt den Service nicht — die UI braucht nur den Zustand.
      password: {
        gesetzt: !!configEntity?.PasswordHash,
        gesetztAm: configEntity?.PasswordSetAt || "",
        gesetztVon: configEntity?.PasswordSetBy || "",
      },
      updatedAt: configEntity?.UpdatedAt || "",
      updatedBy: configEntity?.UpdatedBy || "",
    };
  } catch (error) {
    console.error("Fehler beim Laden der Projektwebsite:", error);
    throw new Error("Die Website-Daten konnten nicht geladen werden.");
  }
};

// method "PUT" = Insert or Replace (alle Properties werden gesetzt),
// method "MERGE" (ohne If-Match) = Insert or Merge (nur die übergebenen Properties).
// Die config-Zeile wird bewusst per MERGE geschrieben, damit ein normales Speichern
// den Passwort-Hash nicht mitlöscht — und umgekehrt.
const putEntity = async (entity, fehlertext, method = "PUT") => {
  const url = `${getBaseUrl()}(PartitionKey='${entity.PartitionKey}',RowKey='${
    entity.RowKey
  }')?${CONFIG.sasToken}`;
  const response = await fetch(url, {
    method,
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json;odata=nometadata",
    },
    body: JSON.stringify(entity),
  });
  if (!response.ok) {
    const errorText = await response.text();
    console.error("Azure Save Error Details:", errorText);
    throw new Error(fehlertext);
  }
  return true;
};

const deleteEntity = async (projectUuid, rowKey) => {
  const url = `${getBaseUrl()}(PartitionKey='${projectUuid}',RowKey='${rowKey}')?${
    CONFIG.sasToken
  }`;
  const response = await fetch(url, {
    method: "DELETE",
    headers: { "If-Match": "*" },
  });
  if (!response.ok && response.status !== 404) {
    throw new Error(`Azure Delete Error: ${response.status}`);
  }
  return true;
};

// Website-Daten eines Projekts speichern: config + Planstand + Adressliste in
// einem Rutsch. Entfernte Adresszeilen werden über `deletedAdressIds` gelöscht
// (das Formular arbeitet lokal und schreibt erst beim Speichern).
export const saveProjectWebsite = async (
  projectUuid,
  { projectCode, projectSlug, blocks, planstand, adressen, deletedAdressIds } = {},
  updatedBy = ""
) => {
  if (!projectUuid) throw new Error("Kein Projekt angegeben.");
  try {
    await ensureTableExists();
    const now = new Date().toISOString();
    const normalizedBlocks = normalizeBlocks(blocks);

    await putEntity(
      {
        PartitionKey: projectUuid,
        RowKey: CONFIG_ROW_KEY,
        Type: "config",
        ProjectCode: cleanValue(projectCode),
        ProjectSlug: cleanValue(projectSlug),
        Blocks: JSON.stringify(normalizedBlocks),
        UpdatedAt: now,
        UpdatedBy: cleanValue(updatedBy),
      },
      "Die Baustein-Einstellungen konnten nicht gespeichert werden.",
      "MERGE" // Passwort-Hash der config-Zeile nicht überschreiben
    );

    await putEntity(
      {
        PartitionKey: projectUuid,
        RowKey: PLANSTAND_ROW_KEY,
        Type: "block",
        BlockKey: "planstand",
        Beschreibung: cleanValue(planstand?.beschreibung),
        Datum: cleanValue(planstand?.datum),
        AblageLink: cleanValue(planstand?.ablageLink),
        UpdatedAt: now,
        UpdatedBy: cleanValue(updatedBy),
      },
      "Der Planstand konnte nicht gespeichert werden."
    );

    const list = Array.isArray(adressen) ? adressen : [];
    for (let i = 0; i < list.length; i += 1) {
      const a = list[i];
      await putEntity(
        {
          PartitionKey: projectUuid,
          RowKey: a.id && a.id.startsWith(ADDRESS_PREFIX) ? a.id : generateAdressId(),
          Type: "address",
          Firma: cleanValue(a.firma),
          Person: cleanValue(a.person),
          Telefon: cleanValue(a.telefon),
          Email: cleanValue(a.email),
          Adresse: cleanValue(a.adresse),
          Ferienvertretung: cleanValue(a.ferienvertretung),
          SortOrder: i,
          UpdatedAt: now,
          UpdatedBy: cleanValue(updatedBy),
        },
        "Die Adressliste konnte nicht gespeichert werden."
      );
    }

    for (const id of deletedAdressIds || []) {
      if (id && id.startsWith(ADDRESS_PREFIX)) {
        await deleteEntity(projectUuid, id);
      }
    }

    return getProjectWebsite(projectUuid);
  } catch (error) {
    console.error("Fehler beim Speichern der Projektwebsite:", error);
    throw new Error(
      error.message || "Die Website-Daten konnten nicht gespeichert werden."
    );
  }
};

// --- Planstand-Register ------------------------------------------------------
// Alle Operationen wirken SOFORT (nicht über den "Speichern"-Button): sobald ein
// Index erfasst ist, existiert sein Link — er wird kopiert und auf Plänen
// verteilt, darf also nicht von einem späteren Speichervorgang abhängen.
//
// Indexstände sind UNVERÄNDERLICH. Ein neuer Index heisst neue Version + neuer
// Link; der alte Link bleibt gültig und gilt als veraltet (US-WEBP-002). Korrektur
// eines Tippfehlers deshalb nur über Löschen des ganzen Plans. Einzige Ausnahme: das
// Datum lässt sich nachträglich korrigieren (siehe `updateWebsitePlanMeta`,
// ADR 0046 Nachtrag) — rein kosmetisch, ändert nie Link oder Index.

const writePlanVersion = async (projectUuid, planId, { index, datum }, erstelltVon) => {
  const linkId = generateLinkId();
  const rowKey = `${PLANVERSION_PREFIX}${linkId}`;
  const now = new Date().toISOString();
  await putEntity(
    {
      PartitionKey: projectUuid,
      RowKey: rowKey,
      Type: "planversion",
      PlanId: cleanValue(planId),
      Index: cleanValue(index),
      Datum: cleanValue(datum),
      PdfBlobName: "", // Platzhalter — PDF-Upload folgt
      ErstelltAm: now,
      ErstelltVon: cleanValue(erstelltVon),
    },
    "Der Indexstand konnte nicht gespeichert werden."
  );
  return { id: rowKey, linkId, planId, index: index || "", datum: datum || "", erstelltAm: now };
};

// Neuen Plan mit erstem Indexstand anlegen.
export const createWebsitePlan = async (
  projectUuid,
  { nummer, titel, quelle, gruppe, index, datum, sortOrder = 0 },
  { projectCode, projectSlug } = {},
  erstelltVon = ""
) => {
  if (!projectUuid) throw new Error("Kein Projekt angegeben.");
  if (!String(nummer || "").trim()) throw new Error("Bitte eine Plannummer erfassen.");
  if (!String(titel || "").trim()) throw new Error("Bitte einen Titel erfassen.");
  if (!String(index || "").trim()) throw new Error("Bitte einen Index erfassen.");

  await ensureTableExists();
  const planId = generatePlanId();
  const now = new Date().toISOString();

  // Reihenfolge: erst die Version schreiben, dann den Plan mit AktuelleVersionId —
  // bricht es dazwischen ab, bleibt eine Waisen-Version ohne Plan zurück, die beim
  // Laden übergangen wird (statt eines Plans ohne jeden Indexstand).
  const version = await writePlanVersion(projectUuid, planId, { index, datum }, erstelltVon);

  await putEntity(
    {
      PartitionKey: projectUuid,
      RowKey: planId,
      Type: "plan",
      ProjectCode: cleanValue(projectCode),
      ProjectSlug: cleanValue(projectSlug),
      Nummer: cleanValue(nummer),
      Titel: cleanValue(titel),
      Quelle: cleanValue(quelle),
      Gruppe: cleanValue(gruppe),
      AktuelleVersionId: version.id,
      SortOrder: Number.isFinite(sortOrder) ? sortOrder : 0,
      ErstelltAm: now,
      ErstelltVon: cleanValue(erstelltVon),
    },
    "Der Plan konnte nicht gespeichert werden."
  );

  return getProjectWebsite(projectUuid);
};

// Neuen Indexstand zu einem bestehenden Plan erfassen (Datum optional). Erzeugt
// einen neuen Link; der bisherige bleibt gültig und gilt danach als veraltet.
export const addWebsitePlanIndex = async (
  projectUuid,
  planId,
  { index, datum },
  erstelltVon = ""
) => {
  if (!projectUuid || !planId) throw new Error("Kein Plan angegeben.");
  if (!String(index || "").trim()) throw new Error("Bitte einen Index erfassen.");

  await ensureTableExists();
  const version = await writePlanVersion(projectUuid, planId, { index, datum }, erstelltVon);

  // MERGE, damit Nummer/Titel/SortOrder unberührt bleiben.
  await putEntity(
    {
      PartitionKey: projectUuid,
      RowKey: planId,
      Type: "plan",
      AktuelleVersionId: version.id,
    },
    "Der neue Indexstand konnte nicht aktiviert werden.",
    "MERGE"
  );

  return getProjectWebsite(projectUuid);
};

// Einen bestehenden Indexstand (wieder) als den aktuellen setzen.
//
// Reparaturweg für den Fall, dass der MERGE nach dem Schreiben einer neuen Version
// abgebrochen ist: dann steht der NEUE Stand als veraltet da und verweist auf einen
// ÄLTEREN als aktuell — die gefährliche Richtung, weil auf der Baustelle dann der
// überholte Plan als gültig erscheint. Deckt auch das Wettrennen ab, wenn zwei
// Personen gleichzeitig einen Index nachziehen (last-write-wins, kein ETag).
//
// Bewusst erlaubt, auch auf einen älteren Stand zurückzustellen: der aktive Stand ist
// eine Entscheidung, nicht das Ergebnis einer Sortierung. Erzeugt KEINE neue Version
// und ändert KEINEN Link — nur, welcher davon als aktuell gilt.
export const setWebsitePlanCurrentVersion = async (
  projectUuid,
  planId,
  versionId
) => {
  if (!projectUuid || !planId || !versionId) {
    throw new Error("Kein Plan oder Indexstand angegeben.");
  }
  const data = await getProjectWebsite(projectUuid);
  const plan = data.plaene.find((p) => p.id === planId);
  if (!plan) throw new Error("Der Plan existiert nicht mehr.");
  if (!plan.versionen.some((v) => v.id === versionId)) {
    throw new Error("Dieser Indexstand gehört nicht zu diesem Plan.");
  }
  await putEntity(
    {
      PartitionKey: projectUuid,
      RowKey: planId,
      Type: "plan",
      AktuelleVersionId: versionId,
      GeaendertAm: new Date().toISOString(),
    },
    "Der Indexstand konnte nicht aktiviert werden.",
    "MERGE"
  );
  return getProjectWebsite(projectUuid);
};

// Nummer/Titel/Quelle/Gruppe eines Plans korrigieren. Ändert KEINE Links — diese
// Angaben gehören zum Plan, nicht zum Indexstand, und erscheinen extern immer in der
// aktuellen Fassung. MERGE, damit nicht übergebene Properties erhalten bleiben;
// eine geleerte Quelle bzw. Gruppe wird aber bewusst als "" geschrieben und damit
// gelöscht (= Plan steht wieder unter "Ohne Gruppe").
//
// `datum`/`versionId` (optional): einzige bewusste Ausnahme von der Unveränderlichkeit
// der Indexstände (ADR 0046, Nachtrag). Korrigiert wird das Datum GENAU des mit
// `versionId` bezeichneten Indexstands per MERGE — Index, LinkId und PdfBlobName
// bleiben unberührt. Zulässig, weil das Datum laut ADR 0046 (Alternative D) ohnehin
// nie darüber entscheidet, welcher Stand aktuell/veraltet ist, und nirgends in Link,
// Index oder QR-Dateiname einfliesst: die Korrektur ist rein kosmetisch (z.B. ein beim
// Erfassen vergessenes oder falsch abgetipptes Datum nachtragen). Fehlt `versionId`,
// bleibt das Datum unangetastet.
export const updateWebsitePlanMeta = async (
  projectUuid,
  planId,
  { nummer, titel, quelle, gruppe, datum, versionId },
  erstelltVon = ""
) => {
  if (!projectUuid || !planId) throw new Error("Kein Plan angegeben.");
  if (!String(nummer || "").trim()) throw new Error("Bitte eine Plannummer erfassen.");
  if (!String(titel || "").trim()) throw new Error("Bitte einen Titel erfassen.");
  await putEntity(
    {
      PartitionKey: projectUuid,
      RowKey: planId,
      Type: "plan",
      Nummer: cleanValue(nummer),
      Titel: cleanValue(titel),
      Quelle: cleanValue(quelle),
      Gruppe: cleanValue(String(gruppe ?? "").trim()),
      GeaendertAm: new Date().toISOString(),
      GeaendertVon: cleanValue(erstelltVon),
    },
    "Der Plan konnte nicht aktualisiert werden.",
    "MERGE"
  );

  if (versionId) {
    await putEntity(
      {
        PartitionKey: projectUuid,
        RowKey: versionId,
        Type: "planversion",
        Datum: cleanValue(datum),
        GeaendertAm: new Date().toISOString(),
        GeaendertVon: cleanValue(erstelltVon),
      },
      "Das Datum des Indexstands konnte nicht korrigiert werden.",
      "MERGE"
    );
  }

  return getProjectWebsite(projectUuid);
};

// Reihenfolge und Gruppenzuteilung des ganzen Registers in einem Zug setzen.
// `ordnung` ist die vollständige, bereits fertig sortierte Liste [{ id, gruppe }];
// `SortOrder` wird daraus lückenlos als 0..n-1 vergeben und entspricht danach
// exakt der Reihenfolge auf dem Bildschirm.
//
// Bewusst EIN Schreibweg für alles, was an der Anordnung dreht: Plan verschieben,
// Plan in eine andere Gruppe ziehen, ganze Gruppe verschieben, Gruppe umbenennen.
// Jede dieser Aktionen erzeugt in der Maske dieselbe Liste — so kann es keine
// zweite Stelle geben, an der Reihenfolge und Gruppenzuteilung auseinanderlaufen.
//
// Links bleiben unberührt: Reihenfolge und Gruppe sind Darstellung, kein Indexstand.
//
// Geschrieben werden nur Pläne, bei denen sich SortOrder oder Gruppe tatsächlich
// ändert. Ein Verschieben um eine Position kostet damit zwei Requests statt n.
// Ein Zug über das ganze Register verschiebt dagegen jede Zeile: die Schreibvorgänge
// laufen deshalb in Gruppen parallel — sie betreffen verschiedene RowKeys und sind
// voneinander unabhängig, eine Reihenfolge gibt es unter ihnen nicht.
const ORDNUNG_PARALLEL = 8;

export const updateWebsitePlanOrder = async (
  projectUuid,
  ordnung,
  erstelltVon = ""
) => {
  if (!projectUuid) throw new Error("Kein Projekt angegeben.");
  const liste = Array.isArray(ordnung) ? ordnung : [];
  const data = await getProjectWebsite(projectUuid);
  const bekannt = new Map(data.plaene.map((p) => [p.id, p]));

  // Gegen den frischen Stand abgleichen statt der Maske blind zu folgen:
  // Pläne, die jemand parallel gelöscht hat, fallen raus; Pläne, die die Maske
  // noch nicht kennt (parallel angelegt), werden hinten angehängt, statt auf
  // SortOrder 0 zusammenzufallen.
  const gewuenscht = liste.filter((e) => e && bekannt.has(e.id));
  const gesehen = new Set(gewuenscht.map((e) => e.id));
  const nachzuegler = data.plaene
    .filter((p) => !gesehen.has(p.id))
    .map((p) => ({ id: p.id, gruppe: p.gruppe }));
  const final = [...gewuenscht, ...nachzuegler];

  const now = new Date().toISOString();
  const zuSchreiben = [];
  for (let i = 0; i < final.length; i += 1) {
    const plan = bekannt.get(final[i].id);
    const gruppe = String(final[i].gruppe ?? "").trim();
    if (plan.sortOrder === i && plan.gruppe === gruppe) continue;
    zuSchreiben.push({
      PartitionKey: projectUuid,
      RowKey: plan.id,
      Type: "plan",
      Gruppe: cleanValue(gruppe),
      SortOrder: i,
      GeaendertAm: now,
      GeaendertVon: cleanValue(erstelltVon),
    });
  }

  // Bricht ein Schreibvorgang ab, kann eine Teilmenge bereits geschrieben sein —
  // die Reihenfolge ist dann gemischt, aber nie kaputt: SortOrder bleibt eine Zahl,
  // die Anzeige bleibt bedienbar, und der nächste Zug schreibt alles neu. Links
  // und Indexstände sind davon in keinem Fall betroffen.
  for (let i = 0; i < zuSchreiben.length; i += ORDNUNG_PARALLEL) {
    await Promise.all(
      zuSchreiben
        .slice(i, i + ORDNUNG_PARALLEL)
        .map((entity) =>
          putEntity(
            entity,
            "Die Reihenfolge konnte nicht gespeichert werden.",
            "MERGE"
          )
        )
    );
  }

  return getProjectWebsite(projectUuid);
};

// Plan samt allen Indexständen löschen. Damit werden auch alle bisherigen Links
// ungültig (404) — bewusst, denn ein gelöschter Plan soll extern nicht mehr als
// "veraltet, neuer Stand vorhanden" erscheinen, sondern verschwinden.
export const deleteWebsitePlan = async (projectUuid, planId) => {
  if (!projectUuid || !planId) throw new Error("Kein Plan angegeben.");
  const data = await getProjectWebsite(projectUuid);
  const plan = data.plaene.find((p) => p.id === planId);
  for (const v of plan?.versionen || []) {
    await deleteEntity(projectUuid, v.id);
  }
  await deleteEntity(projectUuid, planId);
  return getProjectWebsite(projectUuid);
};

// --- Zugangsschutz setzen / entfernen ----------------------------------------
// Wirkt SOFORT (eigener MERGE auf die config-Zeile), unabhängig vom "Speichern"
// des Formulars — ein Passwort soll nicht versehentlich in einem gemischten
// Speichervorgang untergehen. Der Klartext verlässt diese Funktion nicht.
export const setWebsitePassword = async (
  projectUuid,
  plainPassword,
  { projectCode, projectSlug } = {},
  updatedBy = ""
) => {
  if (!projectUuid) throw new Error("Kein Projekt angegeben.");
  const hash = await hashWebsitePassword(plainPassword);
  await ensureTableExists();
  const now = new Date().toISOString();
  await putEntity(
    {
      PartitionKey: projectUuid,
      RowKey: CONFIG_ROW_KEY,
      Type: "config",
      ProjectCode: cleanValue(projectCode),
      ProjectSlug: cleanValue(projectSlug),
      PasswordHash: hash,
      PasswordSetAt: now,
      PasswordSetBy: cleanValue(updatedBy),
    },
    "Das Passwort konnte nicht gespeichert werden.",
    "MERGE"
  );
  return { gesetzt: true, gesetztAm: now, gesetztVon: updatedBy || "" };
};

// Zugangsschutz aufheben. Die Seite ist danach ohne Passwort erreichbar — der
// Microservice muss sie dann als ungeschützt behandeln (siehe Referenz-Dokument).
export const removeWebsitePassword = async (projectUuid, updatedBy = "") => {
  if (!projectUuid) throw new Error("Kein Projekt angegeben.");
  await ensureTableExists();
  await putEntity(
    {
      PartitionKey: projectUuid,
      RowKey: CONFIG_ROW_KEY,
      Type: "config",
      PasswordHash: "",
      PasswordSetAt: "",
      PasswordSetBy: cleanValue(updatedBy),
    },
    "Das Passwort konnte nicht entfernt werden.",
    "MERGE"
  );
  return { gesetzt: false, gesetztAm: "", gesetztVon: "" };
};

// Alle Website-Daten eines Projekts entfernen (wird nicht automatisch beim
// Deaktivieren des GL-Schalters aufgerufen — Deaktivieren blendet die Website
// nur aus, die Inhalte bleiben erhalten).
export const deleteProjectWebsite = async (projectUuid) => {
  const data = await getProjectWebsite(projectUuid);
  await deleteEntity(projectUuid, CONFIG_ROW_KEY);
  await deleteEntity(projectUuid, PLANSTAND_ROW_KEY);
  for (const a of data.adressen) {
    await deleteEntity(projectUuid, a.id);
  }
  for (const plan of data.plaene) {
    for (const v of plan.versionen) {
      await deleteEntity(projectUuid, v.id);
    }
    await deleteEntity(projectUuid, plan.id);
  }
  return true;
};

// Konfiguration aktualisieren (für andere SAS Token etc.)
export const updateConfig = (newConfig) => {
  Object.assign(CONFIG, newConfig);
};
